import * as Common from '@/libs/Common';
import * as DateTimeHelper from '@/libs/DateTimeHelper';
import router from '@/router';
import { RouteUrlList, type IRouteUrlListElement } from '@/libs/RouteList';
import API from '@/api/v1/ServiceAlert';
import { useEsunMB } from '@/composables/useEsunMB';
import { SimplePopUp, SimplePopUpBtnType, SimplePopUpModalType, SimplePopUpSize } from '@/libs/SimplePopUp';

const simplePopUp = new SimplePopUp();

interface ISessionExpireTimer {
    /** 一次性 setTimeout 的 id；undefined 代表沒有排程中 */
    CheckTimeoutId: number | undefined;
    /** 已排程的 expiresAtUtc，用來判斷是否需要重排（同一個值不重排，避免無謂工作） */
    LastScheduledExpiresAt: string | null;
    /** 防止「逾時提示」彈窗被同一輪重複喚起 */
    IsShowingHint: boolean;
    /** 倒數結束後要導向的目的地 */
    RedirectUrl: IRouteUrlListElement;
    /** 是否在 A+ WebView 內（決定逾時時要關 WebView 還是 router.replace） */
    IsInWebView: boolean;

    /**
     * 依絕對到期戳重新排程一次性 timer。
     * - expiresAtUtc 為 null：清掉現有 timer（未登入 / 舊 session）。
     * - 與上次排程同值：沿用，不動現有 timer。
     * - 不同值或第一次：清舊排新，到點時觸發 TimeOutHint。
     * - 已過期（remain <= 0）：立即觸發 TimeOutHint。
     */
    StartDetect(expiresAtUtc: string | null): void;

    /** 清除排程中的 timer，重置狀態（登出 / Session 失效時呼叫） */
    ClearTimeoutId(): void;
}

class SessionExpireTimer implements ISessionExpireTimer {
    CheckTimeoutId: number | undefined;
    LastScheduledExpiresAt: string | null;
    IsShowingHint: boolean;
    RedirectUrl: IRouteUrlListElement;
    IsInWebView: boolean;

    constructor(redirectUrl: IRouteUrlListElement = RouteUrlList.StateError, isInWebView: boolean = false) {
        this.CheckTimeoutId = undefined;
        this.LastScheduledExpiresAt = null;
        this.IsShowingHint = false;
        this.RedirectUrl = redirectUrl;
        this.IsInWebView = isInWebView;
    }

    StartDetect(expiresAtUtc: string | null = null): void {
        // header 缺席：未登入或舊 session — 清除排程，後續若重新登入再由下一個 response 觸發
        if (expiresAtUtc === null) {
            this.ClearTimeoutId();
            return;
        }

        // 彈窗顯示中：避免重排把後續的「使用者點確認 → redirect」流程打斷
        if (this.IsShowingHint) {
            Common.ConsoleLog('[SessionExpireTimer.StartDetect] 正在顯示逾時提示，跳過排程');
            return;
        }

        // 同一個 expiresAtUtc 已排過程：絕對到期值本就不該變，沿用既有 timer
        if (this.LastScheduledExpiresAt === expiresAtUtc && this.CheckTimeoutId !== undefined) {
            return;
        }

        // 清舊排新
        if (this.CheckTimeoutId !== undefined) {
            Common.ConsoleLog(`[SessionExpireTimer.StartDetect] 清除舊 TimeoutId: ${this.CheckTimeoutId}`);
            clearTimeout(this.CheckTimeoutId);
            this.CheckTimeoutId = undefined;
        }

        const expiresAtMs: number = Date.parse(expiresAtUtc);
        if (Number.isNaN(expiresAtMs)) {
            Common.ConsoleLog(`[SessionExpireTimer.StartDetect] expiresAtUtc 解析失敗: ${expiresAtUtc}`);
            return;
        }

        const remainMs: number = expiresAtMs - Date.now();
        this.LastScheduledExpiresAt = expiresAtUtc;

        // 已過期：立即觸發
        if (remainMs <= 0) {
            Common.ConsoleLog('[SessionExpireTimer.StartDetect] expiresAtUtc 已過，立即顯示逾時提示');
            this.TimeOutHint();
            return;
        }

        const appSelf = this;
        this.CheckTimeoutId = window.setTimeout(() => {
            appSelf.TimeOutHint();
        }, remainMs);

        const expireDate: Date = new Date(expiresAtMs);
        Common.ConsoleLog(
            `[SessionExpireTimer.StartDetect] 設置 TimeoutId: ${this.CheckTimeoutId}，將於 ${DateTimeHelper.GetFormatDateTimeString(expireDate)} 觸發逾時提示`
        );
    }

    ClearTimeoutId(): void {
        if (this.CheckTimeoutId !== undefined) {
            Common.ConsoleLog(`[SessionExpireTimer.ClearTimeoutId] 清除 TimeoutId: ${this.CheckTimeoutId}`);
            clearTimeout(this.CheckTimeoutId);
            this.CheckTimeoutId = undefined;
        }
        this.LastScheduledExpiresAt = null;
    }

    /** 顯示絕對到期提示彈窗，使用者點確認或自然關閉後一律 CancelAndRedirect */
    private TimeOutHint(): void {
        if (this.IsShowingHint) {
            Common.ConsoleLog('[SessionExpireTimer.TimeOutHint] 已在顯示，跳過重複呼叫');
            return;
        }
        this.IsShowingHint = true;

        const appSelf = this;
        simplePopUp
            .showAlert(
                SimplePopUpModalType.Warning,
                '系統連線失敗',
                '很抱歉，作業已逾時，請重新登入，造成不便敬請見諒。',
                [
                    {
                        id: 'confirm',
                        type: SimplePopUpBtnType.callback,
                        btnClass: 'btn',
                        resolveParam: 'confirm',
                        title: '確認'
                    }
                ],
                SimplePopUpSize.lg,
                false,
                undefined
            )
            .then(() => {
                appSelf.IsShowingHint = false;
                appSelf.CancelAndRedirect();
            })
            .catch(() => {
                appSelf.IsShowingHint = false;
                appSelf.CancelAndRedirect();
            });
    }

    /** 通知後端登出後，導向 StateError 或關閉 WebView；任何錯誤都不能擋導向 */
    private CancelAndRedirect(): Promise<void> {
        const redirectUrl = this.RedirectUrl;
        const isInWebView = this.IsInWebView;
        const { closeWebView } = useEsunMB();
        const redirect = (): void => {
            if (isInWebView) {
                closeWebView();
            } else {
                router.replace({ name: redirectUrl.name, query: { readlocally: 'y' } });
            }
        };
        return API.IdleTimeout().then(redirect).catch(redirect);
    }
}

export { SessionExpireTimer };
